import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, MapPin, Trophy, ChevronRight, Filter, Plane, Globe, Activity } from 'lucide-react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float } from '@react-three/drei';
import * as THREE from 'three';

const DroneIcon = ({ size = 24, color = "currentColor" }: { size?: number, color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
    <path d="M12 9l0 -4" />
    <path d="M12 15l0 4" />
    <path d="M9 12l-4 0" />
    <path d="M15 12l4 0" />
    <path d="M5 5l2 2" />
    <path d="M19 19l-2 -2" />
    <path d="M19 5l-2 2" />
    <path d="M5 19l2 -2" />
    <path d="M2 5h6" />
    <path d="M2 19h6" />
    <path d="M16 5h6" />
    <path d="M16 19h6" />
  </svg>
);

interface Competition {
  id: number;
  date: string;
  sport: string;
  subSport: string;
  category: string;
  title: string;
  location: string;
  type: string;
  level?: string;
}

const competitionsData: Record<string, Competition[]> = {
  "MART": [
    { id: 1, date: "13-15 Mart 2026", sport: "İnsansız Hava Araçları", subSport: "Drone Soccer", category: "F9A-B", title: "3. Uluslararası Drone Soccer Şampiyonası", location: "Almanya / Aalen", type: "Uluslararası CAT 2" }
  ],
  "NİSAN": [
    { id: 2, date: "04-05 Nisan 2026", sport: "İnsansız Hava Araçları", subSport: "Drone Soccer", category: "F9A-A, F9A-B", title: "Paris Drone Soccer International Cup", location: "Fransa / Paris", type: "Uluslararası CAT 2" },
    { id: 3, date: "18-19 Nisan 2026", sport: "İnsansız Hava Araçları", subSport: "Drone Soccer", category: "F9A-A, F9A-B, F9A-C", title: "Drone Soccer Türkiye Şampiyonası 1. Etap Yarışması", location: "Muhtelif", type: "Ulusal" },
    { id: 4, date: "25 Nisan 2026", sport: "İnsansız Hava Araçları", subSport: "Drone Similasyon", category: "E-Drone Racing", title: "Drone Similasyon 1.Etap Eleme Yarışmaları", location: "Çevrimiçi", type: "Ulusal" }
  ],
  "MAYIS": [
    { id: 10, date: "2 Mayıs 2026", sport: "İnsansız Hava Araçları", subSport: "Drone Similasyon", category: "E-Drone Racing", title: "Drone Similasyon 2.Etap Eleme Yarışması", location: "Çevrimiçi", type: "Ulusal" }
  ],
  "HAZİRAN": [
    { id: 16, date: "6 Haziran 2026", sport: "İnsansız Hava Araçları", subSport: "Drone Racing", category: "F9U", title: "Drone Racing Sıralama Yarışı", location: "Muhtelif", type: "Ulusal" },
    { id: 17, date: "13-14 Haziran 2026", sport: "İnsansız Hava Araçları", subSport: "Drone Soccer", category: "F9A-A, F9A-B, F9A-C", title: "Drone Soccer Türkiye Şampiyonası 2. Etap Yarışması", location: "Muhtelif", type: "Ulusal" },
    { id: 18, date: "15-16 Haziran 2026", sport: "İnsansız Hava Araçları", subSport: "Drone Soccer", category: "F9A-A, F9A-B", title: "Türkiye International Drone Soccer Competition / CAT 2", location: "Muhtelif", type: "Uluslararası CAT 2" },
    { id: 20, date: "27 Haziran 2026", sport: "İnsansız Hava Araçları", subSport: "Drone Racing", category: "F9U", title: "Drone Racing Sıralama Yarışı", location: "Muhtelif", type: "Ulusal" }
  ],
  "TEMMUZ": [
    { id: 23, date: "04 Temmuz 2026", sport: "İnsansız Hava Araçları", subSport: "Drone Racing", category: "F9U", title: "Yeşilay Drone Racing Final Yarışması", location: "Muhtelif", type: "Ulusal" },
    { id: 24, date: "11 Temmuz 2026", sport: "İnsansız Hava Araçları", subSport: "Drone Similasyon", category: "E-Drone Racing", title: "Drone Similasyon Türkiye Finalleri", location: "Çevrimiçi", type: "Ulusal" }
  ],
  "AĞUSTOS": [
    { id: 33, date: "22-23 Ağustos 2026", sport: "İnsansız Hava Araçları", subSport: "Drone Racing", category: "F9U", title: "Türkiye Drone Race 26 Drone Racing World Cup", location: "Muhtelif", type: "Uluslararası CAT 2" }
  ],
  "EYLÜL": [
    { id: 43, date: "19-20 Eylül 2026", sport: "İnsansız Hava Araçları", subSport: "Drone Soccer", category: "F9A-A, F9A-B, F9A-C", title: "Yeşilay Drone Soccer Türkiye Şampiyonası Final", location: "Muhtelif", type: "Ulusal" }
  ],
  "EKİM": [
    { id: 50, date: "Ekim 2026", sport: "İnsansız Hava Araçları", subSport: "Drone Racing", category: "F9U", title: "Drone Racing Dünya Şampiyonası", location: "İstanbul", type: "Uluslararası CAT 1" }
  ]
};

const months = ["MART", "NİSAN", "MAYIS", "HAZİRAN", "TEMMUZ", "AĞUSTOS", "EYLÜL", "EKİM"];

// ─── DRONE ORBIT ──────────────────────────────────────────────────────────────

if (typeof document !== 'undefined' && !document.getElementById('drone-orbit-style-calendar')) {
  const s = document.createElement('style');
  s.id = 'drone-orbit-style-calendar';
  s.textContent = `
    @keyframes droneOrbitCalendar {
      from { transform: rotate(0deg) translateX(var(--r)) rotate(0deg); }
      to   { transform: rotate(360deg) translateX(var(--r)) rotate(-360deg); }
    }
    .drone-orbit-calendar {
      position: absolute;
      top: 50%; left: 50%;
      margin: -8px 0 0 -8px;
      width: 16px; height: 16px;
      animation: droneOrbitCalendar var(--dur, 6s) linear infinite;
      animation-delay: var(--delay, 0s);
    }
  `;
  document.head.appendChild(s);
}

const MiniDroneGeom = () => {
  const ref = React.useRef<THREE.Group>(null);
  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * 1.5;
  });
  return (
    <group ref={ref}>
      <mesh><sphereGeometry args={[0.3, 16, 16]} /><meshStandardMaterial color="#0f2d5e" metalness={0.2} roughness={0.4} /></mesh>
      <mesh><sphereGeometry args={[0.15, 8, 8]} /><meshBasicMaterial color="#4fc3f7" toneMapped={false} /></mesh>
      {[[0.4, 0, 0.4], [-0.4, 0, -0.4], [-0.4, 0, 0.4], [0.4, 0, -0.4]].map((p, i) => (
        <mesh key={i} position={p as any} rotation={[0, i % 2 === 0 ? -Math.PI / 4 : Math.PI / 4, 0]}>
          <boxGeometry args={[0.8, 0.05, 0.1]} /><meshStandardMaterial color="#fff" metalness={0.6} roughness={0.3} />
        </mesh>
      ))}
      {[[0.7, 0.1, 0.7], [-0.7, 0.1, -0.7], [-0.7, 0.1, 0.7], [0.7, 0.1, -0.7]].map((p, i) => (
        <group key={i} position={p as any}>
          <mesh rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.4, 0.04, 8, 24]} /><meshStandardMaterial color="#fff" metalness={0.6} roughness={0.3} /></mesh>
        </group>
      ))}
      <mesh rotation={[0, 0, 0]}><torusGeometry args={[1.2, 0.03, 6, 32]} /><meshStandardMaterial color="#ffffff" metalness={0.5} roughness={0} /></mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[1.2, 0.03, 6, 32]} /><meshStandardMaterial color="#ffffff" metalness={0.5} roughness={0} /></mesh>
      <mesh rotation={[0, Math.PI / 2, 0]}><torusGeometry args={[1.2, 0.03, 6, 32]} /><meshStandardMaterial color="#ffffff" metalness={0.5} roughness={0} /></mesh>
    </group>
  );
};

const SmallDrone = ({ size = 48 }: { size?: number }) => (
  <div style={{ width: size, height: size, flexShrink: 0 }}>
    <Canvas
      camera={{ position: [0, 1, 3], fov: 55 }}
      dpr={1}
      gl={{ antialias: false, powerPreference: 'high-performance', alpha: true }}
      style={{ background: 'transparent' }}
    >
      <ambientLight intensity={0.9} />
      <directionalLight position={[5, 5, 5]} intensity={2} />
      <Float speed={3} floatIntensity={0.5}>
        <group scale={0.65}><MiniDroneGeom /></group>
      </Float>
    </Canvas>
  </div>
);

const YarismaTakvimi: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeMonth, setActiveMonth] = useState<string>("EYLÜL");

  useEffect(() => {
    document.title = "Flying Slap - Yarışma Takvimi";
    window.scrollTo(0, 0);

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="relative text-gray-900 overflow-hidden font-body min-h-screen bg-white" style={{ paddingTop: isMobile ? '120px' : '140px' }}>
      <div className="max-w-7xl mx-auto px-6" style={{ paddingBottom: '100px' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header Section */}
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h1 className="font-headline font-black uppercase tracking-tighter" style={{
              color: '#0f2d5e',
              fontSize: isMobile ? '3.2rem' : 'clamp(3rem, 10vw, 5rem)',
              lineHeight: 0.9,
              marginBottom: '20px'
            }}>
              FAALİYET{isMobile ? <br /> : ' '}
              <span style={{ color: '#e65100' }}>TAKVİMİ</span>
            </h1>
            <div className="mx-auto" style={{
              maxWidth: '800px',
              background: 'rgba(15, 45, 94, 0.03)',
              padding: '24px 32px',
              borderRadius: '24px',
              border: '1px solid rgba(15, 45, 94, 0.08)'
            }}>
              <p style={{ fontSize: '1rem', color: '#4b5563', lineHeight: 1.6, margin: 0 }}>
                <strong style={{ color: '#0f2d5e' }}>Flying Slap olarak 2026 yılında yer alacağımız yarışma ve faaliyet takvimi.</strong><br />
                Kulübümüzün vizyonu doğrultusunda katılacağımız tüm yarışmaları, teknoloji festivallerini ve heyecan dolu turnuvaları buradan takip edebilirsiniz.
              </p>
            </div>
          </div>

          {/* Month Selector */}
          <div style={{
            display: 'flex',
            gap: '10px',
            overflowX: 'auto',
            paddingBottom: '20px',
            marginBottom: '40px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            justifyContent: isMobile ? 'flex-start' : 'center'
          }} className="hide-scrollbar">
            {months.map((month) => (
              <button
                key={month}
                onClick={() => setActiveMonth(month)}
                style={{
                  padding: '12px 24px',
                  borderRadius: '100px',
                  background: activeMonth === month ? '#0f2d5e' : 'rgba(15, 45, 94, 0.05)',
                  color: activeMonth === month ? '#fff' : '#0f2d5e',
                  border: 'none',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  flexShrink: 0,
                  boxShadow: activeMonth === month ? '0 10px 20px rgba(15,45,94,0.2)' : 'none'
                }}
              >
                {month}
              </button>
            ))}
          </div>

          {/* Grid Section */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))', gap: '24px' }}>
            <AnimatePresence mode="wait">
              {competitionsData[activeMonth]?.map((comp, index) => (
                <motion.div
                  key={comp.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  style={{
                    background: '#fff',
                    border: '1px solid rgba(0,0,0,0.06)',
                    borderRadius: '28px',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.03)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* Floating 3D Drone Decoration */}
                  <div
                    className="drone-orbit-calendar"
                    style={{
                      '--r': isMobile ? '120px' : '160px',
                      '--dur': '12s',
                      '--delay': `${index * -2}s`,
                      opacity: 0.7,
                      pointerEvents: 'none',
                      zIndex: 1,
                    } as React.CSSProperties}
                  >
                    <SmallDrone size={isMobile ? 32 : 40} />
                  </div>

                  {index % 3 === 0 && (
                    <div
                      className="drone-orbit-calendar"
                      style={{
                        '--r': isMobile ? '140px' : '190px',
                        '--dur': '18s',
                        '--delay': `${index * -3}s`,
                        opacity: 0.4,
                        pointerEvents: 'none',
                        zIndex: 1,
                      } as React.CSSProperties}
                    >
                      <SmallDrone size={isMobile ? 24 : 30} />
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '14px',
                      background: 'rgba(230, 81, 0, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#e65100'
                    }}>
                      <DroneIcon size={24} color="#e65100" />
                    </div>
                    <div style={{
                      padding: '6px 12px',
                      borderRadius: '100px',
                      background: 'rgba(15, 45, 94, 0.05)',
                      color: '#0f2d5e',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      {comp.type}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#9ca3af', marginBottom: '4px' }}>
                      {comp.sport} / {comp.subSport}
                    </div>
                    <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f2d5e', lineHeight: 1.3 }}>
                      {comp.title}
                    </h3>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#4b5563', fontSize: '0.9rem' }}>
                      <Calendar size={16} color="#e65100" />
                      <strong>{comp.date}</strong>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#4b5563', fontSize: '0.9rem' }}>
                      <MapPin size={16} color="#e65100" />
                      <span>{comp.location}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Empty State */}
          {(!competitionsData[activeMonth] || competitionsData[activeMonth].length === 0) && (
            <div style={{ textAlign: 'center', padding: '80px 0', color: '#9ca3af' }}>
              <Calendar size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
              <p>Bu ay için henüz planlanmış bir faaliyet bulunmamaktadır.</p>
            </div>
          )}

          {/* Footer Note */}
          <div style={{ textAlign: 'center', marginTop: '80px', padding: '30px', borderRadius: '24px', background: 'rgba(230, 81, 0, 0.03)' }}>
            <p style={{ color: '#4b5563', fontSize: '0.9rem', margin: 0 }}>
              <strong style={{ color: '#e65100' }}>Önemli Not:</strong> Bu liste Türkiye Hava Sporları Federasyonu'nun 2026 yılı taslak faaliyet takvimidir. Tarihler ve konumlar organizasyon komitesi tarafından güncellenebilir.
            </p>
          </div>
        </motion.div>
      </div>
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

export default YarismaTakvimi;
